import {
  Flex,
  FormControl,
  Input,
  InputGroup,
  InputLeftElement,
  SlideFade,
  Stack,
  Text,
  useColorModeValue,
  Select,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { AiOutlinePhone } from "react-icons/ai";
import Container from "../../components/Container";
import { PrimaryButton } from "../../components/CustomButton";
import { errorNotifier } from "../../components/NotificationHandler";
import { Link } from "react-router-dom";
import { COLORS } from "../../constants/colors";
import { PUBLIC_PATHS } from "../../app/constants";
import { fetchStreetsData, signupTenant } from "./service/signup";

const { LOGIN } = PUBLIC_PATHS;

const Signup = () => {
  const cardBg = useColorModeValue(COLORS.primary, COLORS.white);
  const pageBg = useColorModeValue(COLORS.white, COLORS.black);

  // Signup form states
  const [streetId, setStreetId] = useState("");
  const [houseId, setHouseId] = useState(""); // Changed from houseNumber to houseId
  const [displayHouseNumber, setDisplayHouseNumber] = useState(""); // For display purposes
  const [tenantName, setTenantName] = useState("");
  const [tenantPhone, setTenantPhone] = useState("");
  const [email, setEmail] = useState("");
  const [designation, setDesignation] = useState("");
  const [loading, setLoading] = useState(false);
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [plateNumber, setPlateNumber] = useState("");

  // Backend data states
  const [streetsData, setStreetsData] = useState({});
  const [housesMap, setHousesMap] = useState({}); // Map to store house number -> house ID mapping
  const [loadingStreets, setLoadingStreets] = useState(true);

  // Fetch streets and house numbers from backend on component mount
// Fetch streets and house numbers from backend on component mount
useEffect(() => {
  console.log("Starting to fetch streets data...");
  let isSubscribed = true;

  const loadStreetsData = async () => {
    try {
      await fetchStreetsData(
        (data) => {
          if (isSubscribed) {
            console.log("Received streets data:", data);
            setStreetsData(data);
            
            // Create a house mapping for each street using the data returned by fetchStreetsData
            const houseMapping = {};
            Object.entries(data).forEach(([streetId, streetData]) => {
              houseMapping[streetId] = streetData.houseDetails || {};
            });
            setHousesMap(houseMapping);
          }
        },
        (loading) => {
          if (isSubscribed) {
            setLoadingStreets(loading);
          }
        }
      );
    } catch (error) {
      console.error("Failed to fetch streets data:", error);
      if (isSubscribed) {
        setLoadingStreets(false);
        errorNotifier("Could not load streets data.");
      }
    }
  };

  loadStreetsData();

  return () => {
    isSubscribed = false;
  };
}, []);

  // Debug log when streetsData changes
  useEffect(() => {
    console.log("Current streetsData state:", streetsData);
  }, [streetsData]);

  const handleStreetChange = (e) => {
    const selectedStreetId = e.target.value;
    setStreetId(selectedStreetId); // Set the street ID to be sent to the backend
    setHouseId(""); // Reset house ID when street changes
    setDisplayHouseNumber(""); // Reset display house number
  };

  const handleHouseChange = (e) => {
    const selectedHouseNumber = e.target.value;
    setDisplayHouseNumber(selectedHouseNumber);
    
    // Get the house ID from our mapping
    if (housesMap[streetId] && housesMap[streetId][selectedHouseNumber]) {
      setHouseId(housesMap[streetId][selectedHouseNumber]);
    } else {
      // Fallback to using the house number if mapping fails
      setHouseId(selectedHouseNumber);
      console.warn("House ID mapping not found for", selectedHouseNumber, "using house number instead");
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    const payload = {
      houseId: houseId, // Send the house ID instead of number
      streetId,
      houseUnit: "",
      tenantName,
      tenantPhone,
      email,
      designation,
      type: vehicleType,
      model: vehicleModel,
      plateNumber: plateNumber,
    };

    if (!houseId || !streetId || !tenantName || !tenantPhone || !email || !designation ) {
      errorNotifier("Please fill in all fields");
      return;
    }

    const success = await signupTenant(payload, setLoading);
    if (success) {
      window.location.href = LOGIN;
    }
  };

  return (
    <SlideFade in={true} offsetX="-5000px">
      <Container bg={pageBg}>
        <Stack w="100%" as="section" pt="15vh" h="100%">
          <Text color={cardBg} fontWeight="600" fontSize="2.7rem">
            Sign Up
          </Text>
          <Text color={cardBg} fontWeight="500" fontSize="1.2rem">
            Kindly fill up the details below
          </Text>
          <FormControl w="100%">
            <Flex my="7" gap="4">
              {/* Street Select */}
              <InputGroup flex="1">
                <Select
                  {...inputStyles}
                  borderColor={COLORS.green}
                  placeholder="Select Street"
                  _placeholder={{ color: COLORS.green }}
                  value={streetId}
                  onChange={handleStreetChange}
                  isDisabled={loadingStreets}
                >
                  {Object.values(streetsData)
                    .sort((a, b) => a.streetName.localeCompare(b.streetName))
                    .map(({ streetName, streetId }) => (
                      <option key={streetId} value={streetId}>
                        {streetName}
                      </option>
                    ))}
                </Select>
              </InputGroup>

              {/* House Number Select */}
              <InputGroup flex="1">
                <Select
                  {...inputStyles}
                  borderColor={COLORS.green}
                  placeholder="Select House"
                  _placeholder={{ color: COLORS.green }}
                  value={displayHouseNumber}
                  onChange={handleHouseChange}
                  isDisabled={!streetId}
                >
                  {streetId &&
                    streetsData[streetId]?.houses.map((number) => (
                      <option key={number} value={number}>
                        {number}
                      </option>
                    ))}
                </Select>
              </InputGroup>
            </Flex>

            {/* Designation Select Field */}
            <InputGroup my="7">
              <Select
                {...inputStyles}
                borderColor={COLORS.green}
                placeholder="Designation"
                _placeholder={{ color: COLORS.green }}
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
              >
                <option value="tenant">Tenant</option>
                <option value="landlord">Landlord</option>
                <option value="agent">Agent</option>
                <option value="business">Business</option>
              </Select>
            </InputGroup>

            {/* Full Name Input Field */}
            <InputGroup my="7">
              <Input
                {...inputStyles}
                borderColor={COLORS.green}
                placeholder="Full Name"
                _placeholder={{ color: COLORS.green }}
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
              />
            </InputGroup>

            {/* Phone Number Input Field */}
            <InputGroup my="7">
              <Input
                {...inputStyles}
                borderColor={COLORS.green}
                placeholder="Phone Number"
                _placeholder={{ color: COLORS.green }}
                value={tenantPhone}
                type="number"
                onChange={(e) => setTenantPhone(e.target.value)}
              />
              <InputLeftElement children={<AiOutlinePhone />} mt="10px" />
            </InputGroup>

            {/* Email Input Field */}
            <InputGroup my="7">
              <Input
                {...inputStyles}
                borderColor={COLORS.green}
                placeholder="Email"
                _placeholder={{ color: COLORS.green }}
                value={email}
                type="email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </InputGroup>
            <Flex my="7" gap="4">
            {/* Vehicle Type Input Field */}
            <InputGroup flex="1">
              <Select
                {...inputStyles}
                borderColor={COLORS.green}
                placeholder="Vehicle Type"
                _placeholder={{ color: COLORS.green }}
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
              >
                <option value="car">Car</option>
                <option value="motorcycle">Motorcycle</option>
                <option value="truck">Truck</option>
                <option value="bus">Bus</option>
                <option value="other">Other</option>
              </Select>
            </InputGroup>

            {/* Vehicle Model Input Field */}
            <InputGroup flex="1">
              <Input
                {...inputStyles}
                borderColor={COLORS.green}
                placeholder="Vehicle Model"
                _placeholder={{ color: COLORS.green }}
                value={vehicleModel}
                onChange={(e) => setVehicleModel(e.target.value)}
              />
            </InputGroup>
            </Flex>

            {/* Plate Number Input Field */}
            <InputGroup my="7">
              <Input
                {...inputStyles}
                borderColor={COLORS.green}
                placeholder="Plate Number"
                _placeholder={{ color: COLORS.green }}
                value={plateNumber}
                onChange={(e) => setPlateNumber(e.target.value)}
              />
            </InputGroup>

            {/* Signup Button */}
            <PrimaryButton
              loadingText="Signing you up"
              btnText="Sign up"
              isLoading={loading}
              onClick={handleSignup}
              h="60px"
              fontSize="18px"
              rounded="2xl"
            />

            {/* Login Link */}
            <Text
              fontSize="16px"
              textAlign="center"
              fontWeight={600}
              color={cardBg}
              mb="20px"
            >
              Already have an account?{" "}
              <Link
                to={LOGIN}
                style={{ color: COLORS.green, textDecoration: "underline" }}
              >
                Log In
              </Link>
            </Text>
          </FormControl>
        </Stack>
      </Container>
    </SlideFade>
  );
};

export default Signup;

export const inputStyles = {
  bg: "white",
  rounded: "2xl",
  h: "60px",
  color: "#262636",
};